import { all, call, put, SagaGenerator, select } from 'typed-redux-saga';
import { v4 as uuid } from 'uuid';

import {
    getAppVersionSelector,
    selectIsGoloomRc,
    selectTelemostHost,
} from '../../../../../shared/features/config/selectors';
import { createLogger } from '../../../../../shared/helpers/loggerFacade';
import {
    retryWithTimeout,
    RetryWithTimeoutError,
    RetryWithTimeoutParams,
} from '../../../../../shared/helpers/redux-saga';
import { cloudApi } from '../../../../../shared/helpers/transport';
import { IDEMPOTENCY_KEY_HEADER } from '../../../../../shared/helpers/transport/cloud-api/transport';
import { CloudAPI } from '../../../../../shared/helpers/transport/cloud-api/types';
import { pickQueryParams } from '../../../../../shared/helpers/url';
import { normalizeConferenceUri } from '../../../helpers/conference-config';
import { handleFetchError } from '../../../helpers/handlers-sagas';
import { ConferenceConfig } from '../../../types';
import { setRoomConferenceConfig } from '../slice';
import { fetchMeSaga } from './me';

const FETCH_CONFIG_MAX_ATTEMPTS = 3;
const YT_TOKEN_QUERY = 'yt-token';

const logger = createLogger('goloom:conference-config');

export function* updateConferenceConfig(
    roomId: string,
    searchString?: string,
    displayName?: string
): SagaGenerator<ConferenceConfig> {
    try {
        const telemostHost = yield* select(selectTelemostHost);
        const isGoloomRc = yield* select(selectIsGoloomRc);
        const appVersion = yield* select(getAppVersionSelector);

        const queryParams = pickQueryParams(searchString ?? '', [YT_TOKEN_QUERY]);
        const queryString = queryParams ? `?${queryParams.toString()}` : '';

        const connectRequest: CloudAPI.ConnectToConference.Request = {
            roomId,
            telemostHost,
            displayName,
            isGoloomRc,
            appVersion,

            searchString: queryString,
            enableGoloom: true,
            additionalHeaders: { [IDEMPOTENCY_KEY_HEADER]: uuid() },
        };

        const { conference, me } = yield* all({
            conference: call(
                [cloudApi, retryWithTimeout],
                {
                    maxTries: FETCH_CONFIG_MAX_ATTEMPTS,
                    onAttemptError: function* (error, attempt) {
                        logger.warn(error, {
                            method: 'updateConferenceConfig',
                            additional: {
                                retryN: attempt,
                                reason: error instanceof Error ? error.message : undefined,
                            },
                        });
                        return yield* call(handleFetchError, error);
                    },
                } as RetryWithTimeoutParams<typeof cloudApi.connectToConference>,
                cloudApi.connectToConference,
                connectRequest
            ) as SagaGenerator<CloudAPI.ConnectToConference.Response | undefined>,
            me: displayName ? call(() => undefined) : call(fetchMeSaga),
        });

        if (!conference) {
            throw Error('Did not receive conference config');
        }

        const conferenceUri = conference.uri ? conference.uri : `https://${telemostHost}/j/${roomId}`;

        // TODO: кажется (а Кирилл говорит, что не кажется) display_name в конфиге - пережиток MVP1. Нужно убрать
        const config: ConferenceConfig = {
            ...conference,
            resolved_uri: normalizeConferenceUri(`${conferenceUri}${queryString}`),
            ...(me?.displayName ? { display_name: me.displayName } : {}),
        };

        yield* put(setRoomConferenceConfig(config));

        return config;
    } catch (error) {
        if (error instanceof RetryWithTimeoutError) {
            throw RetryWithTimeoutError.enhance(error, {
                additional: {
                    // попытки считаем с 0, потому вычитаем
                    retryN: FETCH_CONFIG_MAX_ATTEMPTS - 1,
                    reason: error.message,
                },
            });
        }

        throw error;
    }
}
